/**
 * ChartCard -- Card shell for chart content with title, actions, and loading state.
 * @layer atom
 * @package @plerity/ui-core
 */

import { memo } from "react";
import { DemoBadge, useDemoMode } from "../DemoMode/DemoMode";
import { cn } from "../utils/cn";
import type { ChartCardProps } from "./ChartCard.types";

export const ChartCard = memo(function ChartCard({
  title,
  subtitle,
  children,
  loading = false,
  className,
  actions,
  height = 300,
  headingLevel = 3,
}: ChartCardProps) {
  const isDemo = useDemoMode();
  const Heading = `h${headingLevel}` as const;
  return (
    <div
      className={cn(
        // `min-w-0` lets the chart card shrink inside flex/grid parents
        // so its long titles truncate instead of pushing the layout wider.
        //
        // Literal white/gray-800 surface — do NOT use `bg-surface` or
        // `border-[var(--color-border-default)]` here. Those emit `var(--color-*)`
        // with NO fallback and are stripped from the React SPA apps' Tailwind v4
        // build (`:root` blocks in `@import`-ed colors.css are dropped). The literal
        // ramp keeps fallbacks (`var(--color-gray-800,#1e293b)`) so dark mode renders
        // true-dark and survives the strip.
        "min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800",
        className
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-2">
        {/* `min-w-0 flex-1` is the canonical "let me truncate inside a flex row" pattern. */}
        <div className="min-w-0 flex-1">
          <Heading className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100" title={title}>
            {title}
          </Heading>
          {subtitle && (
            // `text-muted` (NOT bare text-gray-500): the web gray ramp is static,
            // so gray-500 (#64748b) was 3.07:1 on the dark card (#1e293b) — AA
            // fail (2026-10-11 AI visual QA). text-muted = var(--color-text-muted,
            // #475569): literal fallback survives the v4 strip, and the injected
            // var flips per mode (6.41:1 light / 6.55:1 dark) — no dark: pair needed.
            <p className="mt-0.5 truncate text-xs text-muted" title={subtitle}>
              {subtitle}
            </p>
          )}
        </div>
        {(actions || isDemo) && (
          <div className="flex shrink-0 items-center gap-2">
            {isDemo && <DemoBadge />}
            {actions}
          </div>
        )}
      </div>

      <div style={{ height }} className="w-full">
        {loading ? (
          <output className="flex h-full items-center justify-center" aria-label="Loading chart">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-brand dark:border-gray-700" />
          </output>
        ) : (
          children
        )}
      </div>
    </div>
  );
});

ChartCard.displayName = "ChartCard";
